import React, { useState, useEffect } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { StripeCardElementOptions, PaymentRequest } from '@stripe/stripe-js';

interface StripePaymentFormProps {
  onSubmit: (paymentMethod: any) => Promise<void>;
  isProcessing: boolean;
  error: string | null;
  onErrorClear: () => void;
  amount: number;
  currency?: string;
}

const StripePaymentForm: React.FC<StripePaymentFormProps> = ({
  onSubmit,
  isProcessing,
  error,
  onErrorClear,
  amount,
  currency = 'usd'
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [cardComplete, setCardComplete] = useState(false);
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(null);
  const [canMakePayment, setCanMakePayment] = useState(false);
  const [paymentRequestLoading, setPaymentRequestLoading] = useState(false);

  const cardElementOptions: StripeCardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
        iconColor: '#424770',
      },
      invalid: {
        iconColor: '#9e2146',
      },
    },
  };

  useEffect(() => {
    if (!stripe) return;

    const pr = stripe.paymentRequest({
      country: 'US',
      currency: currency.toLowerCase(),
      total: {
        label: 'Subscription',
        amount: amount * 100, // Convert to cents
      },
      requestPayerName: true,
      requestPayerEmail: true,
    });

    pr.on('paymentmethod', async (event) => {
      setPaymentRequestLoading(true);
      try {
        await onSubmit(event.paymentMethod);
      } catch (error) {
        console.error('Payment failed:', error);
      } finally {
        setPaymentRequestLoading(false);
      }
    });

    pr.on('cancel', () => {
      setPaymentRequestLoading(false);
    });

    pr.canMakePayment().then((result) => {
      if (result) {
        setCanMakePayment(true);
        setPaymentRequest(pr);
      }
    });
  }, [stripe, amount, currency, onSubmit]);

  const handleApplePayGooglePay = () => {
    if (paymentRequest) {
      paymentRequest.show();
    }
  };

  const handleSubmit = async (e: React.MouseEvent) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    try {
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardElement)!,
      });

      if (error) {
        console.error('Payment method creation failed:', error);
        onErrorClear();
        return;
      }

      if (paymentMethod) {
        await onSubmit(paymentMethod);
      }
    } catch (err) {
      console.error('Payment submission error:', err);
      onErrorClear();
    }
  };

  const handleCardChange = (event: any) => {
    setCardComplete(event.complete);
    if (error) {
      onErrorClear();
    }
  };

  return (
    <div className="stripe-payment-container">
      {error && (
        <div className="error-banner">
          <span className="error-message">{error}</span>
          <button 
            type="button" 
            onClick={onErrorClear}
            className="error-close"
          >
            ×
          </button>
        </div>
      )}
      
      {/* Apple Pay / Google Pay Button */}
      {canMakePayment && paymentRequest && (
        <div className="payment-request-button-container">
          <button
            type="button"
            onClick={handleApplePayGooglePay}
            disabled={isProcessing || paymentRequestLoading}
            className="btn apple-google-pay"
          >
            {paymentRequestLoading ? 'Processing...' : 'Pay with Apple Pay / Google Pay'}
          </button>
          <div className="payment-divider">
            <span className="divider-line"></span>
            <span className="divider-text">or pay with card</span>
            <span className="divider-line"></span>
          </div>
        </div>
      )}
      
      {/* Card Payment Form */}
      <div className="card-payment-form">
        <div className="card-element-container">
          <CardElement
            options={cardElementOptions}
            onChange={handleCardChange}
          />
        </div>
        
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!stripe || !cardComplete || isProcessing}
          className="btn primary"
        >
          {isProcessing ? 'Processing...' : 'Complete Payment'}
        </button>
      </div>
    </div>
  );
};

export default StripePaymentForm; 